import { useUser } from "contexts/UserContext";
import { capitalize } from "lodash";
import moment from "moment-timezone";
import ClearInputIcon from "notes-and-forms-v2/icons/ClearInputIcon";
import SignHereIcon from "notes-and-forms-v2/icons/SignHereIcon";
import { useState } from "react";
import { Button } from "react-bootstrap";
import RequiredAsterisk from "../../required-asterisk/RequiredAsterisk";
import { ElementViewProps } from "../AtomicElementView";
import styles from "./SignatureContent.module.scss";
import getClasses from "utils/getClasses";

// Signature should be stored in templateData.signatures[elementId], not in templateData[elementId] or templateData[sectionId][elementId]
// It is a breaking change compared to the initial implementation, so on the slim chance someone actually used it between the initial implementation and this change, we will check for both
function parseLegacyData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {} as SignatureData;
  }

  return data as SignatureData;
}

function getMinorName(user: User | null) {
  const minor = user?.minors?.find(
    ({ minor }) => minor.orchid_user_id === user?.current_minor_user_id,
  )?.minor;

  if (!minor) {
    return "";
  }

  return [minor.preferred_first_name ?? minor.first_name, minor.last_name]
    .filter(Boolean)
    .join(" ");
}

export default function SignatureContent({
  data: rawLegacyData, // This is the old way to store signature data
  signatureData, // This is the new way to store signature data
  element,
  onChange: setLegacyData, // Only used to clear the old signature
  onSignatureChange,
  isLocked,
  isPrintMode,
  disabled,
  showAsMissing,
}: ElementViewProps<SignatureElement>) {
  const data = signatureData ?? parseLegacyData(rawLegacyData);

  const { defaultUserTimezone, user } = useUser();
  const [isSigning, setIsSigning] = useState(false);

  const shouldShowInput = isSigning || data.signature?.type === "text";
  const minorName = getMinorName(user ?? null);
  const signedByLabel = capitalize(element.signedBy);

  if (isPrintMode || isLocked || disabled) {
    return (
      <div className={styles.container}>
        <div className={styles.label}>
          {element.isRequired && <RequiredAsterisk />}
          {signedByLabel} Signature:
        </div>
        <div className={styles.signatureLineWrapper}>
          <div className={styles.signatureWrapper}>
            {data.signature?.type === "text" && (
              <div className={styles.signatureInput}>
                {data.signature?.type === "text" ? data.signature.value : ""}
              </div>
            )}
          </div>
          <div className={styles.signatureLine} />
        </div>
        {data.dateSigned && (
          <div className={styles.signedOn}>
            Signed on:{" "}
            {moment
              .tz(data.dateSigned, defaultUserTimezone)
              .format("MMMM D,  YYYY")}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.label}>
        {element.isRequired && <RequiredAsterisk />}
        {signedByLabel} Signature:
      </div>
      <div className={styles.signatureLineWrapper}>
        <div className={styles.signatureWrapper}>
          {shouldShowInput ? (
            <>
              <input
                className={styles.signatureInput}
                autoFocus={!data.signature}
                value={
                  data.signature?.type === "text" ? data.signature.value : ""
                }
                onChange={(event) => {
                  if (!event.target.value) {
                    onSignatureChange(element.id, {});
                    return;
                  }
                  onSignatureChange(element.id, {
                    dateSigned: new Date().toISOString(),
                    signature: {
                      type: "text",
                      value: event.target.value,
                    },
                  });
                }}
              />
              <button
                className={styles.clearInputButton}
                onClick={() => {
                  setIsSigning(false);
                  onSignatureChange(element.id, {});
                  setLegacyData({});
                }}
              >
                <ClearInputIcon />
              </button>
            </>
          ) : (
            <>
              <Button
                className={styles.signHereButton}
                onClick={() => setIsSigning(true)}
              >
                Sign Here <SignHereIcon />
              </Button>
              <div style={{ flex: 1 }} />
            </>
          )}
        </div>
        <div
          className={getClasses(
            styles.signatureLine,
            showAsMissing && !data.signature && styles.missing,
          )}
        />
      </div>
      {minorName && (
        <div className={styles.onBehalf}>
          You are signing on behalf of {minorName}
        </div>
      )}
      {data.dateSigned && (
        <div className={styles.signedOn}>
          Signed on:{" "}
          {moment
            .tz(data.dateSigned, defaultUserTimezone)
            .format("MMMM D,  YYYY")}
        </div>
      )}
    </div>
  );
}
